<template>
  <div v-if="data" class="relative h-full pb-4 overflow-x-auto bg-white">
    <table class="w-full text-sm text-left text-gray-900 divide-y divide-gray-200">
      <tbody>
        <BaseDetailsTableRow
          :name="$t('baseObject.code')"
          :mode="mode"
          :isHorizontalLayout="isHorizontalLayout">
          <BaseInput
            :mode="mode"
            v-model="data.code"
            color="base"
            flat
            show-error-as-tooltip
            :error="codeErrorMessage"
            :disabled="mode == 'view'" />
        </BaseDetailsTableRow>

        <BaseDetailsTableRow
          :mode="mode"
          :name="$t('competitor.profession')"
          :isHorizontalLayout="isHorizontalLayout">
          <BaseInput
            :mode="mode"
            v-model="data.profession"
            color="base"
            flat
            :disabled="mode == 'view'" />
        </BaseDetailsTableRow>
        <BaseDetailsTableRow
          :name="$t('competitor.competitorType')"
          :mode="mode"
          :isHorizontalLayout="isHorizontalLayout">
          <BaseSelectDropdown
            :items="dropdownCompetitorTypes"
            :selected-item="selectedCompetitorType"
            @update:selected="handleCompetitorTypeSelection"
            name="functionality"
            :mode="mode"
            clearable
            show-error-as-tooltip
            :error="competitorTypeErrorMessage" />
        </BaseDetailsTableRow>

        <BaseDetailsTableRow
          :mode="mode"
          :name="$t('competitor.remarks')"
          :isHorizontalLayout="isHorizontalLayout">
          <BaseInput
            :mode="mode"
            v-model="data.remarks"
            color="base"
            flat
            :disabled="mode == 'view'" />
        </BaseDetailsTableRow>

        <BaseDetailsTableRow
          :name="$t('baseObject.isActive')"
          :mode="mode"
          :isHorizontalLayout="isHorizontalLayout">
          <BaseTableCellStatus v-if="mode == 'view'" :item="data.isActive ? data.isActive : 0" />
          <BaseToggle v-else name="isActive" v-model="data.isActive" />
        </BaseDetailsTableRow>

        <BaseDetailsTableRow
          :name="$t('baseObject.boid')"
          :mode="mode"
          :isHorizontalLayout="isHorizontalLayout">
          {{ data.boid }}
        </BaseDetailsTableRow>

        <BaseDetailsTableRow
          :name="$t('baseObject.isFromManager')"
          :mode="mode"
          :isHorizontalLayout="isHorizontalLayout">
          <BaseTableCellStatus
            :item="data.isFromManager ? data.isFromManager : 0"
            :textTrue="$t('common.actions.yes')"
            :textFalse="$t('common.actions.no')" />
        </BaseDetailsTableRow>

        <BaseDetailsTableRow
          :name="$t('baseObject.lastUpdate')"
          :mode="mode"
          :isHorizontalLayout="isHorizontalLayout">
          {{ isoDateToFullDate(data.lastUpdate) || '-' }}
        </BaseDetailsTableRow>

        <BaseDetailsTableRow
          :name="$t('baseObject.updateUser')"
          :mode="mode"
          :isHorizontalLayout="isHorizontalLayout">
          {{ data.updateUser?.name || '-' }}
        </BaseDetailsTableRow>

        <BaseDetailsTableRow
          :name="$t('baseObject.createdDate')"
          :mode="mode"
          :isHorizontalLayout="isHorizontalLayout">
          {{ isoDateToFullDate(data.createdDate) || '-' }}
        </BaseDetailsTableRow>

        <BaseDetailsTableRow
          :name="$t('baseObject.createUser')"
          :mode="mode"
          :isHorizontalLayout="isHorizontalLayout">
          {{ data.createUser?.name || '-' }}
        </BaseDetailsTableRow>
      </tbody>
    </table>
  </div>
</template>

<script setup lang="ts">
import { isoDateToFullDate } from '../../utils/date';
import BaseTableCellStatus from '../common/BaseTableCellStatus.vue';
import BaseInput from '../common/BaseInput.vue';
import BaseDetailsTableRow from '../common/BaseDetailsTableRow.vue';
import BaseSelectDropdown from '../common/BaseSelectDropdown.vue';
import type { SelectDropdownItem } from '../../types/Base';
import BaseToggle from '../common/BaseToggle.vue';
import type { Competitor } from '../../types/Traders';
import { onMounted, ref, watch } from 'vue';
import { storeToRefs } from 'pinia';
import { useLookupsStore } from '../../stores/lookups';

const props = defineProps<{
  mode: 'view' | 'edit';
  isHorizontalLayout: boolean;
  codeErrorMessage: string;
  competitorTypeErrorMessage: string;
}>();

const data = defineModel<Competitor>();
const selectedCompetitorType = ref<SelectDropdownItem | null>(null);
const { dropdownCompetitorTypes } = storeToRefs(useLookupsStore());
const { getCompetitorTypesLookup } = useLookupsStore();

onMounted(async () => {
  await initDropdowns();
});

watch(
  () => data.value,
  () => {
    initDropdowns();
  },
  { deep: true }
);

watch(
  () => props.codeErrorMessage,
  () => {
    console.log(props.codeErrorMessage);
  },
  { deep: true }
);

async function initDropdowns() {
  if (data.value) {
    await getCompetitorTypesLookup();
    const competitorType = dropdownCompetitorTypes.value.find(
      (item) => item.value.toString().toLowerCase() == data.value?.competitorTypeId.toLowerCase()
    );
    if (competitorType) selectedCompetitorType.value = competitorType;
  }
}

function handleCompetitorTypeSelection(item: SelectDropdownItem) {
  selectedCompetitorType.value = item ? item : null;
  if (data.value) {
    data.value.competitorTypeId = item ? (item.value as string) : '';
    if (item?.value) {
      const competitorType = dropdownCompetitorTypes.value.find(
        (item) => item.value == data.value?.competitorTypeId
      );
      if (competitorType) data.value.competitorType = competitorType;
    }
  }
}
</script>
